/**
 * RootErrorBoundary — last-resort catch-all around the whole app.
 *
 * Real bug this fixes: the deployed app's own post-authentication landing route
 * (`/post-login` — see `@passport/pages`' `PostLoginPage`/`ReturnUrlRedirectHandler`'s
 * bare-origin fallback, which nginx resolves to this app as the default landing page on
 * some deployments) rendered a completely blank page — flat background, no header, no
 * sidebar, no canvas, no error, no loading indicator. Root cause: nothing in this app's
 * render tree (main.tsx -> App -> ConfiguredApp -> SsoAuthGate -> WorkflowApp -> AppLayout
 * -> UnifiedDashboard, etc.) was ever wrapped in a React error boundary, so ANY uncaught
 * exception thrown during render anywhere in that deep tree (a malformed API response, a
 * null-pointer in a child component, a failed dynamic import) unmounts React's entire
 * output. index.css's `html, body { background: rgb(2, 6, 23) !important; }` is the only
 * thing left on screen afterward — exactly the dark blue-gray flat background reported,
 * with the tab title untouched because that's static markup in index.html, not something
 * React controls.
 *
 * `ConfigurationErrorScreen` in App.tsx already covers the ONE specific failure this app
 * used to guard against (a bad VITE_TENANT_ID thrown at module-evaluation time, before
 * React ever mounts) — this component is the general-purpose backstop for every OTHER
 * exception, thrown at any depth, at any point after React does mount.
 */

import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

export class RootErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Goes to the console/wherever the browser's error reporting is wired up — this is the
    // only diagnostic trail available in production, where the screen below intentionally
    // doesn't show the raw error (see ConfigurationErrorScreen's own DEV-only detail gate).
    console.error('[RootErrorBoundary] Uncaught render error:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.error) {
      const detail = import.meta.env.DEV ? String(this.state.error.stack ?? this.state.error) : null;
      return (
        <div
          role="alert"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            width: '100%',
            padding: '24px',
            textAlign: 'center',
            fontFamily: 'system-ui, sans-serif',
            background: 'rgb(2, 6, 23)',
            color: '#f8fafc',
          }}
        >
          <h1 style={{ fontSize: 20, margin: 0 }}>Something went wrong</h1>
          <p style={{ marginTop: 8, color: '#94a3b8', maxWidth: 480 }}>
            Flow Studio ran into an unexpected error and couldn't finish loading this page.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              marginTop: 16,
              padding: '10px 20px',
              borderRadius: 8,
              border: 'none',
              background: '#309B48',
              color: '#fff',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
          {detail && (
            <pre
              style={{
                marginTop: 20,
                maxWidth: '90vw',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                textAlign: 'left',
                fontSize: 12,
                color: '#94a3b8',
              }}
            >
              {detail}
            </pre>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default RootErrorBoundary;
